import axios from 'axios';

// Determine backend URL based on environment
// Uses environment variable with fallback to current origin
const getBackendUrl = () => {
  // Use environment variable if set, otherwise use current origin
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Utility to extract error message from API response
// Handles both string errors and Pydantic validation error arrays
export const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors are arrays of objects with 'msg' field
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

// Check if error is an authentication error
export const isAuthError = (error) => {
  const status = error.response?.status;
  // Only 401 (Unauthorized) is a true auth error requiring logout
  // 403 (Forbidden) means user is authenticated but lacks permission
  return status === 401;
};

// Track if interceptors have been setup to prevent duplicates
let interceptorsSetup = false;

// Setup axios interceptors for global error handling
export const setupAxiosInterceptors = (logoutCallback) => {
  // Prevent duplicate interceptor registration
  if (interceptorsSetup) {
    return;
  }
  interceptorsSetup = true;
  
  // Response interceptor to handle auth errors globally
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      
      // Handle authentication errors (401 Unauthorized ONLY)
      // 403 Forbidden means user IS authenticated but lacks permission - don't logout
      // Only 401 means the token is invalid/expired and user should be logged out
      if (status === 401) {
        const currentPath = window.location.pathname;
        const isLoginRequest = error.config?.url?.includes('/auth/login');
        const isRegisterRequest = error.config?.url?.includes('/auth/register');
        
        // Don't redirect if:
        // - Already on login page
        // - It's a login/register request (let the form handle the error)
        if (currentPath !== '/login' && !isLoginRequest && !isRegisterRequest) {
          console.warn('Authentication error detected (401), redirecting to login');
          // Clear stored token
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          // Call logout callback if provided
          if (logoutCallback) {
            logoutCallback();
          }
          // Redirect to login
          window.location.href = '/login';
        }
      }
      
      return Promise.reject(error);
    }
  );
  
  // Request interceptor to ensure token is always set
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token && !config.headers['Authorization']) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

// Auth API
export const authApi = {
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }),
  register: (data) => axios.post(`${API}/auth/register`, data),
  getMe: () => axios.get(`${API}/auth/me`),
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  resetPassword: (token, newPassword) => axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword }),
};

// Users API
export const usersApi = {
  getAll: () => axios.get(`${API}/users`),
  getById: (id) => axios.get(`${API}/users/${id}`),
  recruiterWorkload: () => axios.get(`${API}/users-meta/recruiter-workload`),
  create: (data) => axios.post(`${API}/users`, data),
  update: (id, data) => axios.put(`${API}/users/${id}`, data),
  updateRole: (id, role) => axios.put(`${API}/users/${id}/role?role=${role}`),
  resetPassword: (id, newPassword) => axios.post(`${API}/users/${id}/reset-password`, { new_password: newPassword }),
  delete: (id, reassignTo = null) =>
    axios.delete(`${API}/users/${id}`, {
      params: reassignTo ? { reassign_to: reassignTo } : undefined,
    }),
  changePassword: (currentPassword, newPassword) => 
    axios.post(`${API}/users/change-password`, { current_password: currentPassword, new_password: newPassword }),
  getRoles: () => axios.get(`${API}/users/roles/list`),
};

// Clients API
export const clientsApi = {
  getAll: (params = {}) => axios.get(`${API}/clients`, { params }),
  getById: (id) => axios.get(`${API}/clients/${id}`),
  create: (data) => axios.post(`${API}/clients`, data),
  update: (id, data) => axios.put(`${API}/clients/${id}`, data),
  delete: (id) => axios.delete(`${API}/clients/${id}`),
  getIndustries: () => axios.get(`${API}/industries`),
};

// Jobs API
export const jobsApi = {
  getAll: (params = {}) => axios.get(`${API}/jobs`, { params }),
  getById: (id) => axios.get(`${API}/jobs/${id}`),
  getClosures: (id) => axios.get(`${API}/jobs/${id}/closures`),
  create: (data) => axios.post(`${API}/jobs`, data),
  update: (id, data) => axios.put(`${API}/jobs/${id}`, data),
  updateStatus: (id, status) => axios.put(`${API}/jobs/${id}/status?status=${status}`),
  delete: (id) => axios.delete(`${API}/jobs/${id}`),
  // Workflow endpoints
  submitForApproval: (id, remarks = null) => 
    axios.post(`${API}/jobs/${id}/submit-for-approval${remarks ? `?remarks=${encodeURIComponent(remarks)}` : ''}`),
  approve: (id, remarks = null) => 
    axios.post(`${API}/jobs/${id}/approve${remarks ? `?remarks=${encodeURIComponent(remarks)}` : ''}`),
  reject: (id, remarks = null) => 
    axios.post(`${API}/jobs/${id}/reject${remarks ? `?remarks=${encodeURIComponent(remarks)}` : ''}`),
  publish: (id) => axios.post(`${API}/jobs/${id}/publish`),
  assignRecruiters: (id, recruiterIds) => 
    axios.post(`${API}/jobs/${id}/assign-recruiters`, { recruiter_ids: recruiterIds }),
  getWorkflowStatuses: () => axios.get(`${API}/jobs/workflow/statuses`),
  // AI Matching
  matchCandidates: (id, limit = 20) => axios.post(`${API}/jobs/${id}/match-candidates?limit=${limit}`),
  // Quick Match (rule-based, instant)
  quickMatchCandidates: (id, limit = 20) => axios.post(`${API}/jobs/${id}/quick-match-candidates?limit=${limit}`),
  parsePdf: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/jobs/parse-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Manager / Team-Lead view: single-screen job allocation summary + recruiter load
  allocationSummary: () => axios.get(`${API}/job-allocation/summary`),
  // Manager / Team-Lead view: every recruiter's monthly closure goal progress (one round-trip)
  teamGoalProgress: () => axios.get(`${API}/job-allocation/team-goal-progress`),
  // Manager / Team-Lead view: set or clear a recruiter's per-user monthly closure goal
  setRecruiterGoal: (userId, goal) =>
    axios.put(`${API}/users/${userId}/monthly-closure-goal`, { goal }),
  // Manager / Team-Lead view: set or clear the org-wide default monthly closure goal.
  // overridePersonal=true also wipes per-user overrides so everyone lands on the new default.
  setOrgGoal: (goal, overridePersonal = false) =>
    axios.put(`${API}/organizations/me/monthly-closure-goal`, {
      goal,
      override_personal: overridePersonal,
    }),
  // Per-recruiter monthly closure goal — feeds the dashboard banner
  goalProgress: () => axios.get(`${API}/dashboard/goal-progress`),
  // Job analytics — top-strip and per-job summary drawer on /jobs page
  analyticsSummary: (period = 'all_time') =>
    axios.get(`${API}/jobs-analytics/summary`, { params: { period } }),
  clientLocationMatrix: (metric = 'vacancies', period = 'all_time') =>
    axios.get(`${API}/jobs-analytics/client-location`, { params: { metric, period } }),
  recruiterLocationMatrix: (metric = 'joined_ctc', period = 'all_time') =>
    axios.get(`${API}/jobs-analytics/recruiter-location`, { params: { metric, period } }),
  recruiterClientMatrix: (metric = 'joined_ctc', period = 'all_time', join_basis = 'stage_event') =>
    axios.get(`${API}/jobs-analytics/recruiter-client`, { params: { metric, period, join_basis } }),
  joinedByIndustry: (period = 'all_time') =>
    axios.get(`${API}/jobs-analytics/joined-by-industry`, { params: { period } }),
  unclassifiedClients: () =>
    axios.get(`${API}/jobs-analytics/unclassified-clients`),
  patchClientIndustry: (clientId, industry) =>
    axios.patch(`${API}/clients/${clientId}/industry`, { industry }),
  clientStageMatrix: (period = 'all_time', range = null) =>
    axios.get(`${API}/pipeline-analytics/client-stage`, {
      params:
        period === 'custom' && range && range.start_date && range.end_date
          ? { period: 'custom', start_date: range.start_date, end_date: range.end_date }
          : { period },
    }),
  jobStageMatrix: (period = 'all_time', range = null, status = 'open') =>
    axios.get(`${API}/pipeline-analytics/job-stage`, {
      params:
        period === 'custom' && range && range.start_date && range.end_date
          ? { period: 'custom', start_date: range.start_date, end_date: range.end_date, status }
          : { period, status },
    }),
  summary: (id) => axios.get(`${API}/jobs/${id}/summary`),
  aiSummary: (id) => axios.post(`${API}/jobs/${id}/ai-summary`),
};

export const incentiveSettingsApi = {
  get: () => axios.get(`${API}/settings/incentive`),
  put: (pct) => axios.put(`${API}/settings/incentive`, { incentive_pct_of_ctc: pct }),
};

// Per-recruiter performance dashboard
export const recruiterPerformanceApi = {
  // user_id may be 'me' or a UUID. Period: month | quarter | year | all.
  // `range` (optional): { start_date, end_date } ISO date strings — when both
  // provided the backend uses them as a custom window and ignores `period`.
  get: (userId = 'me', period = 'month', range = null) =>
    axios.get(`${API}/recruiters/${userId}/performance`, {
      params:
        range && range.start_date && range.end_date
          ? { start_date: range.start_date, end_date: range.end_date }
          : { period },
    }),
  // List of users the caller can switch the picker to. Admins get the whole
  // org; managers get their reports_to subtree; recruiters get just themselves.
  visible: () => axios.get(`${API}/recruiters/visible`),
};

// Team-lead / manager consolidated view — direct reportees only
export const myTeamApi = {
  // Direct reportees of the caller
  reportees: () => axios.get(`${API}/users/me/reportees`),
  // Team aggregate + per-reportee breakdown.
  // `mode`: direct | tree (whole subtree under the manager)
  // `compare`: when true, decorates response with prev-period KPIs + signed deltas
  // `range`: optional { start_date, end_date } — overrides period when both set.
  // `scope`: team (default — manager + reportees rollup) | self (just the
  //          manager's own personal recruitment numbers). Useful for a
  //          recruiting TL/DM who wants to see only their personal stats.
  summary: (
    managerId = 'me',
    period = 'month',
    { mode = 'direct', compare = false, range = null, scope = 'team' } = {}
  ) => {
    const params = { manager_id: managerId, mode, compare, scope };
    if (range && range.start_date && range.end_date) {
      params.start_date = range.start_date;
      params.end_date = range.end_date;
    } else {
      params.period = period;
    }
    return axios.get(`${API}/my-team/summary`, { params });
  },
};

// Client SPOC API
export const clientSpocApi = {
  assignSpoc: (clientId, userId) => axios.post(`${API}/clients/${clientId}/assign-spoc?user_id=${userId}`),
  getClientSpocs: (clientId) => axios.get(`${API}/clients/${clientId}/spocs`),
  removeSpoc: (clientId, userId) => axios.delete(`${API}/clients/${clientId}/spocs/${userId}`),
  getUserAssignments: (userId) => axios.get(`${API}/users/${userId}/client-assignments`),
};

// Candidates API
export const candidatesApi = {
  getAll: (params = {}) => axios.get(`${API}/candidates`, { params }),
  getById: (id) => axios.get(`${API}/candidates/${id}`),
  applicationsSummary: (id) => axios.get(`${API}/candidates/${id}/applications-summary`),
  getResume: (id) => axios.get(`${API}/candidates/${id}/resume`, { responseType: 'blob' }),
  create: (data) => axios.post(`${API}/candidates`, data),
  update: (id, data) => axios.put(`${API}/candidates/${id}`, data),
  delete: (id) => axios.delete(`${API}/candidates/${id}`),
  // iter_42: Duplicate detection & cleanup
  duplicatesReport: (limitClusters = 50) => axios.get(`${API}/candidates/duplicates-report`, { params: { limit_clusters: limitClusters } }),
  mergeDuplicates: (keepId, deleteIds) => axios.post(`${API}/candidates/merge-duplicates`, { keep_id: keepId, delete_ids: deleteIds }),
  // iter_49: One-shot DOB backfill (admin-only)
  backfillDob: (limit = 500, dryRun = false) => axios.post(`${API}/candidates/backfill-dob`, { limit, dry_run: dryRun }),
  uploadResume: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/candidates/upload-resume`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadCandidateResume: (candidateId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/candidates/${candidateId}/upload-resume`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  parseResume: (resumeText) => axios.post(`${API}/candidates/parse-resume?resume_text=${encodeURIComponent(resumeText)}`),
  reparseResume: (candidateId, forceOcr = true) =>
    axios.post(`${API}/candidates/${candidateId}/reparse-resume?force_ocr=${forceOcr}`, null, {
      timeout: 120000, // OCR + AI parse can take up to ~90s on a multi-page scanned PDF
    }),
  bulkUpload: (files, onProgress, overwrite = false) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    return axios.post(`${API}/candidates/bulk-upload?overwrite=${overwrite}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
      timeout: 600000, // 10 minutes timeout for bulk uploads
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
  },
  bulkReuploadResumes: (files, onProgress) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    return axios.post(`${API}/candidates/bulk-reupload-resumes`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
      timeout: 900000, // 15 minutes timeout for bulk re-uploads
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
  },
  bulkUploadExcel: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/candidates/bulk-upload-excel`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
      timeout: 1200000, // 20 minutes timeout for large Excel files
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
  },
  downloadExcelTemplate: () => axios.get(`${API}/candidates/excel-template`, { responseType: 'blob' }),
  advancedSearch: (params) => {
    const queryParams = new URLSearchParams();
    if (params.query) queryParams.append('query', params.query);
    if (params.minExp) queryParams.append('min_exp', params.minExp);
    if (params.maxExp) queryParams.append('max_exp', params.maxExp);
    if (params.minCurrentCtc) queryParams.append('min_current_ctc', params.minCurrentCtc);
    if (params.maxCurrentCtc) queryParams.append('max_current_ctc', params.maxCurrentCtc);
    if (params.minExpectedCtc) queryParams.append('min_expected_ctc', params.minExpectedCtc);
    if (params.maxExpectedCtc) queryParams.append('max_expected_ctc', params.maxExpectedCtc);
    if (params.maxNoticePeriod) queryParams.append('max_notice_period', params.maxNoticePeriod);
    if (params.education) queryParams.append('education', params.education);
    if (params.location) queryParams.append('location', params.location);
    if (params.minAge != null && params.minAge !== '') queryParams.append('min_age', params.minAge);
    if (params.maxAge != null && params.maxAge !== '') queryParams.append('max_age', params.maxAge);
    queryParams.append('limit', params.limit || 100);
    return axios.post(`${API}/candidates/search?${queryParams.toString()}`);
  },
  checkDuplicates: (data) => axios.post(`${API}/candidates/check-duplicates`, data),
  findSimilar: (id) => axios.get(`${API}/candidates/${id}/find-similar`),
  // AI Matching
  matchJobs: (id, limit = 10) => axios.post(`${API}/candidates/${id}/match-jobs?limit=${limit}`),
  matchToJob: (candidateId, jobId) => axios.post(`${API}/match/candidate-to-job?candidate_id=${candidateId}&job_id=${jobId}`),
  // Admin: Scan/cleanup invalid resume references (legacy data)
  cleanupInvalidResumes: (dryRun = true) =>
    axios.post(`${API}/candidates/cleanup-invalid-resumes?dry_run=${dryRun}`),
  // Workbench inline edit (partial update — RBAC-aware on backend)
  workbenchUpdate: (id, data) => axios.put(`${API}/workbench/candidates/${id}`, data),
  // Autocomplete: top distinct candidate locations in caller's org (with counts)
  distinctLocations: (q = '', limit = 50) =>
    axios.get(`${API}/candidates/distinct-locations`, { params: { ...(q ? { q } : {}), limit } }),
};

// Saved Searches API (for candidate searches)
export const savedSearchesApi = {
  getAll: () => axios.get(`${API}/saved-searches`),
  getById: (id) => axios.get(`${API}/saved-searches/${id}`),
  create: (data) => axios.post(`${API}/saved-searches`, data),
  update: (id, data) => axios.put(`${API}/saved-searches/${id}`, data),
  delete: (id) => axios.delete(`${API}/saved-searches/${id}`),
  run: (id, limit = 100) => axios.post(`${API}/saved-searches/${id}/run?limit=${limit}`),
};

// Job Match Searches API (for job matching saved searches)
export const jobMatchSearchesApi = {
  getAll: () => axios.get(`${API}/job-match-searches`),
  getById: (id) => axios.get(`${API}/job-match-searches/${id}`),
  create: (data) => axios.post(`${API}/job-match-searches`, data),
  delete: (id) => axios.delete(`${API}/job-match-searches/${id}`),
  run: (id, limit = 20) => axios.post(`${API}/job-match-searches/${id}/run?limit=${limit}`),
};

// Applications API
export const applicationsApi = {
  getAll: (params = {}) => axios.get(`${API}/applications`, { params }),
  getById: (id) => axios.get(`${API}/applications/${id}`),
  create: (data) => axios.post(`${API}/applications`, data),
  changeStage: (id, data) => axios.put(`${API}/applications/${id}/stage`, data),
  delete: (id) => axios.delete(`${API}/applications/${id}`),
  bulkShortlist: (data) => axios.post(`${API}/applications/bulk-shortlist`, data),
  bulkChangeStage: (data) => axios.post(`${API}/applications/bulk-stage-change`, data),
  assign: (id, assigned_to) => axios.patch(`${API}/applications/${id}/assign`, { assigned_to }),
};

// Interviews API
export const interviewsApi = {
  getAll: (params = {}) => axios.get(`${API}/interviews`, { params }),
  getById: (id) => axios.get(`${API}/interviews/${id}`),
  create: (data) => axios.post(`${API}/interviews`, data),
  update: (id, data) => axios.put(`${API}/interviews/${id}`, data),
  submitFeedback: (id, data) => axios.put(`${API}/interviews/${id}/feedback`, data),
  delete: (id) => axios.delete(`${API}/interviews/${id}`),
  createCalendarEvent: (interviewId, attendees = []) => 
    axios.post(`${API}/calendar/create-event`, { interview_id: interviewId, attendees }),
  updateCalendarEvent: (interviewId) => axios.put(`${API}/calendar/event/${interviewId}`),
  deleteCalendarEvent: (interviewId) => axios.delete(`${API}/calendar/event/${interviewId}`),
};

// Google Calendar API
export const calendarApi = {
  getAuthUrl: () => axios.get(`${API}/calendar/auth-url`),
  getStatus: () => axios.get(`${API}/calendar/status`),
  disconnect: () => axios.delete(`${API}/calendar/disconnect`),
};

// Notifications API
export const notificationsApi = {
  getAll: () => axios.get(`${API}/notifications`),
  markRead: (id) => axios.put(`${API}/notifications/${id}/read`),
  markAllRead: () => axios.put(`${API}/notifications/read-all`),
};

// Reports API
export const reportsApi = {
  dropReasonsTrend: (months = 12) =>
    axios.get(`${API}/reports/drop-reasons-trend`, { params: { months } }),
  offerToJoinByClient: (months = 12, limit = 10) =>
    axios.get(`${API}/reports/offer-to-join-by-client`, { params: { months, limit } }),
  offerToJoinClientDetails: (clientId, months = 12) =>
    axios.get(`${API}/reports/offer-to-join-by-client/${clientId}/details`, { params: { months } }),
  naukriUploads: (period = 'all_time', startDate = null, endDate = null) =>
    axios.get(`${API}/reports/naukri-uploads`, {
      params: {
        period,
        ...(period === 'custom' && startDate ? { start_date: startDate } : {}),
        ...(period === 'custom' && endDate ? { end_date: endDate } : {}),
      },
    }),
  sourceUploads: (period = 'all_time', startDate = null, endDate = null) =>
    axios.get(`${API}/reports/source-uploads`, {
      params: {
        period,
        ...(period === 'custom' && startDate ? { start_date: startDate } : {}),
        ...(period === 'custom' && endDate ? { end_date: endDate } : {}),
      },
    }),
};

// Dashboard API
export const dashboardApi = {
  getStats: (params = {}) => axios.get(`${API}/dashboard/stats`, { params }),
  getRecentActivity: (params = {}) => axios.get(`${API}/dashboard/recent-activity`, { params }),
  getClosureHealth: (period = 'month', joinBasis = null, opts = {}) =>
    axios.get(`${API}/dashboard/closure-health`, {
      params: { period, ...(joinBasis ? { join_basis: joinBasis } : {}) },
      ...opts,
    }),
  getClosureJoinedCandidates: (period = 'month', limit = 200, joinBasis = null) =>
    axios.get(`${API}/dashboard/closure-health/joined-candidates`, {
      params: { period, limit, ...(joinBasis ? { join_basis: joinBasis } : {}) },
    }),
  getClosureLaggards: (period = 'month', limit = 5) =>
    axios.get(`${API}/dashboard/closure-health/laggards`, {
      params: { period, limit },
    }),
  nudgeRecruiter: (userId, period = 'month', message = null) =>
    axios.post(`${API}/dashboard/closure-health/nudge`, {
      user_id: userId,
      period,
      message,
    }),
  getAutoNudge: () => axios.get(`${API}/organizations/me/auto-nudge`),
  setAutoNudge: (enabled, thresholdPct = null) =>
    axios.put(`${API}/organizations/me/auto-nudge`, {
      enabled,
      ...(thresholdPct !== null ? { threshold_pct: thresholdPct } : {}),
    }),
  getClosureOfWeek: () => axios.get(`${API}/organizations/me/closure-of-week`),
  setClosureOfWeek: (enabled) =>
    axios.put(`${API}/organizations/me/closure-of-week`, { enabled }),
  getJoiningThisWeek: (days = 7) =>
    axios.get(`${API}/dashboard/joining-this-week`, { params: { days } }),
};

// Command Center API (live metrics + tasks + recent activity)
export const commandCenterApi = {
  getMetrics: (params = {}) => axios.get(`${API}/command-center/metrics`, { params }),
  celebrateWin: (payload) => axios.post(`${API}/recruiter/wins/celebrate`, payload),
  getDailyTasks: (params = {}) => axios.get(`${API}/command-center/daily-tasks`, { params }),
  getRecentActivity: (limit = 50, extraParams = {}) =>
    axios.get(`${API}/command-center/recent-activity`, { params: { limit, ...extraParams } }),
  getBrokenResumesCount: () =>
    axios.get(`${API}/command-center/broken-resumes-count`),
  getMissingCtc: (limit = 50) =>
    axios.get(`${API}/command-center/missing-ctc`, { params: { limit } }),
  setApplicationOfferedCtc: (appId, offeredCtc) =>
    axios.patch(`${API}/applications/${appId}/offered-ctc`, { offered_ctc: offeredCtc }),
  setApplicationOfferLetterDate: (appId, offerLetterDate) =>
    axios.patch(`${API}/applications/${appId}/offer-letter-date`, {
      offer_letter_date: offerLetterDate, // YYYY-MM-DD or null to clear
    }),
  setApplicationJoiningDate: (appId, joiningDate) =>
    axios.patch(`${API}/applications/${appId}/joining-date`, { joining_date: joiningDate }),
  getMetricItems: (metric, limit = 50, range = null) =>
    axios.get(`${API}/command-center/metrics/items`, {
      params: {
        metric,
        limit,
        ...(range && range.start_date && range.end_date
          ? { start_date: range.start_date, end_date: range.end_date }
          : {}),
      },
    }),
  getStageCandidates: (stage, jobId = null, limit = 200) =>
    axios.get(`${API}/command-center/stage-candidates`, {
      params: { stage, ...(jobId && jobId !== 'all' ? { job_id: jobId } : {}), limit },
    }),
};

// Organisation / plan API
export const orgApi = {
  getMine: () => axios.get(`${API}/organizations/me`),
  getUsage: () => axios.get(`${API}/organizations/me/usage`),
  update: (data) => axios.put(`${API}/organizations/me`, data),
  // Slack Incoming Webhook management — webhook URL is never echoed back; the
  // API returns {connected, host, channel} so we can render a safe status row.
  setSlackWebhook: (webhookUrl) =>
    axios.put(`${API}/organizations/me/slack-webhook`, { webhook_url: webhookUrl || null }),
  testSlackWebhook: () =>
    axios.post(`${API}/organizations/me/slack-webhook/test`),
  // Org-wide default lens for Offer→Joining analytics card. Admin-only.
  setDefaultJoinBasis: (basis) =>
    axios.put(`${API}/organizations/me/default-join-basis`, { default_join_basis: basis }),
};

// In-app support tickets
export const supportApi = {
  create: (data) => axios.post(`${API}/support/tickets`, data),
  listMine: () => axios.get(`${API}/support/tickets`),
  listAll: (status) =>
    axios.get(`${API}/admin/support/tickets`, {
      params: status ? { status } : {},
    }),
  resolve: (id) => axios.put(`${API}/admin/support/tickets/${id}/resolve`),
};

// Audit Logs API
export const auditApi = {
  getLogs: (limit = 100, userId = null, action = null, range = null) => {
    const params = { limit };
    if (userId) params.user_id = userId;
    if (action) params.action = action;
    if (range && range.start_date && range.end_date) {
      params.start_date = range.start_date;
      params.end_date = range.end_date;
    }
    return axios.get(`${API}/audit-logs`, { params });
  },
  // Distinct action strings present in the caller's org — populates the filter dropdown.
  distinctActions: () => axios.get(`${API}/audit-logs/distinct-actions`),
};

// Activity Logs API
export const activityApi = {
  getLogs: (limit = 100, userId = null, category = null) => {
    const params = { limit };
    if (userId) params.user_id = userId;
    if (category) params.category = category;
    return axios.get(`${API}/activity-logs`, { params });
  },
  getUserSummary: (startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return axios.get(`${API}/activity-logs/user-summary`, { params });
  },
  getRecruiterTaskSummary: (startDate = null, endDate = null, userId = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (userId) params.user_id = userId;
    return axios.get(`${API}/activity-logs/recruiter-task-summary`, { params });
  },
};

// Workbench API (Recruiter Workbench)
export const workbenchApi = {
  // Statuses
  getStatuses: () => axios.get(`${API}/workbench/statuses`),
  // Remarks
  addRemark: (candidateId, remark, isPrivate = true, status = null) => 
    axios.post(`${API}/workbench/remarks`, { candidate_id: candidateId, remark, is_private: isPrivate, status }),
  getMyRemarks: (candidateId = null) => {
    const params = candidateId ? { candidate_id: candidateId } : {};
    return axios.get(`${API}/workbench/remarks`, { params });
  },
  getCandidateRemarks: (candidateId) => axios.get(`${API}/workbench/remarks/${candidateId}`),
  updateRemark: (remarkId, candidateId, remark, isPrivate, status = null) => 
    axios.put(`${API}/workbench/remarks/${remarkId}`, { candidate_id: candidateId, remark, is_private: isPrivate, status }),
  deleteRemark: (remarkId) => axios.delete(`${API}/workbench/remarks/${remarkId}`),
  // My Candidates
  getMyCandidates: (bucket = 'active') =>
    axios.get(`${API}/workbench/my-candidates`, { params: { bucket } }),
  // Remove candidate from workbench (archive)
  removeCandidate: (candidateId) => axios.post(`${API}/workbench/remove-candidate/${candidateId}`),
  // Update candidate from workbench (excludes phone number)
  updateCandidate: (candidateId, data) => axios.put(`${API}/workbench/candidates/${candidateId}`, data),
  // Available candidates (not in others' pipeline)
  getAvailableCandidates: (params = {}) => axios.get(`${API}/candidates/available`, { params }),
};

// Analytics API. Each endpoint accepts a `period` (legacy enum) OR a custom
// `start_date` + `end_date` ISO range. Custom range takes precedence on the
// backend when both bounds are provided.
const _analyticsParams = (period, range) => {
  if (range && range.start_date && range.end_date) {
    return { start_date: range.start_date, end_date: range.end_date };
  }
  return { period };
};
export const analyticsApi = {
  getAllRecruitersPerformance: (period = 'month', range = null) =>
    axios.get(`${API}/analytics/recruiters`, { params: _analyticsParams(period, range) }),
  getRecruiterDetails: (recruiterId, period = 'month', range = null) =>
    axios.get(`${API}/analytics/recruiter/${recruiterId}`, { params: _analyticsParams(period, range) }),
  getTeamSummary: (period = 'month', range = null) =>
    axios.get(`${API}/analytics/team-summary`, { params: _analyticsParams(period, range) }),
};

// AI Features API
export const aiApi = {
  // Generate job description from minimal input
  generateJobDescription: (data) => axios.post(`${API}/ai/generate-job-description`, data),
  
  // Get AI-powered candidate summary
  getCandidateSummary: (candidateId) => axios.get(`${API}/ai/candidate/${candidateId}/summary`),
  
  // Get job recommendations for a candidate
  getCandidateRecommendations: (candidateId, limit = 5) => 
    axios.get(`${API}/ai/candidate/${candidateId}/recommendations?limit=${limit}`),
  
  // Get skill gap analysis for a job
  getSkillGapAnalysis: (jobId) => axios.get(`${API}/ai/job/${jobId}/skill-gap-analysis`),
  
  // Get hiring predictions for a job
  getHiringPredictions: (jobId) => axios.get(`${API}/ai/job/${jobId}/hiring-predictions`),
  
  // Batch score candidates against a job
  batchScoreCandidates: (jobId, candidateIds = null, limit = 10) => {
    const params = new URLSearchParams({ job_id: jobId, limit });
    if (candidateIds) {
      candidateIds.forEach(id => params.append('candidate_ids', id));
    }
    return axios.post(`${API}/ai/candidates/batch-score?${params.toString()}`);
  },
};

export default {
  auth: authApi,
  users: usersApi,
  clients: clientsApi,
  jobs: jobsApi,
  candidates: candidatesApi,
  applications: applicationsApi,
  interviews: interviewsApi,
  notifications: notificationsApi,
  dashboard: dashboardApi,
  audit: auditApi,
  analytics: analyticsApi,
  calendar: calendarApi,
  savedSearches: savedSearchesApi,
  ai: aiApi,
  commandCenter: commandCenterApi,
  org: orgApi,
  support: supportApi,
  recruiterPerformance: recruiterPerformanceApi,
};
